import { useState } from "react"
import { useQuery, useMutation } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { pondsApi } from "../api/pondsApi"
import { queryKeys, queryClient } from "@/shared/api/queryClient"
import { useFarmFromUrl, usePondFromUrl } from "@/shared/hooks/useUrlSync"
import { Card } from "@/shared/components/ui/Card"
import { Button } from "@/shared/components/ui/Button"
import { Badge } from "@/shared/components/ui/Badge"
import { PageLoading, SectionLoading } from "@/shared/components/ui/LoadingSpinner"
import { FarmRequiredGuard } from "@/shared/components/FarmRequiredGuard"
import { formatDate, cn } from "@/shared/lib/utils"
import type { PondCreateRequest, PondUpdateRequest } from "@/shared/types/api"

// Zod schema for pond update validation
const pondUpdateSchema = z.object({
  name: z.string()
    .min(2, "Pond name must be at least 2 characters")
    .max(100, "Pond name must be less than 100 characters")
    .trim(),
  description: z.string()
    .max(255, "Description must be less than 255 characters")
    .optional(),
})

type PondUpdateFormData = z.infer<typeof pondUpdateSchema>

export function PondsPage() {
  // Get farm and pond from URL params
  const { farmId, farm, isLoading: isFarmLoading, isError: isFarmError, error: farmError } = useFarmFromUrl()
  const { pondId, pond, navigateToPond, closePond } = usePondFromUrl(farmId)

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [createStep, setCreateStep] = useState<1 | 2>(1)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [newPondName, setNewPondName] = useState("")
  const [pondShape, setPondShape] = useState<'rectangular' | 'circular' | 'irregular'>('rectangular')
  const [newPondLength, setNewPondLength] = useState("")
  const [newPondWidth, setNewPondWidth] = useState("")
  const [newPondDiameter, setNewPondDiameter] = useState("")
  const [newPondSurfaceArea, setNewPondSurfaceArea] = useState("") // Only for irregular
  const [newPondVolume, setNewPondVolume] = useState("") // Only for irregular
  const [newPondDepth, setNewPondDepth] = useState("")
  const [newPondDescription, setNewPondDescription] = useState("")
  const [newPondFishType, setNewPondFishType] = useState("")
  const [newPondWaterType, setNewPondWaterType] = useState<'fresh' | 'salt' | 'brackish'>('fresh')

  // Calculate volume and surface area from dimensions
  const calculatedSurfaceArea = () => {
    if (pondShape === 'rectangular') {
      const length = parseFloat(newPondLength) || 0
      const width = parseFloat(newPondWidth) || 0
      return length * width
    } else if (pondShape === 'circular') {
      const diameter = parseFloat(newPondDiameter) || 0
      const radius = diameter / 2
      return Math.PI * radius * radius
    } else if (pondShape === 'irregular') {
      return parseFloat(newPondSurfaceArea) || 0
    }
    return 0
  }

  const calculatedVolume = () => {
    const surfaceArea = calculatedSurfaceArea()
    const depth = parseFloat(newPondDepth) || 0
    return surfaceArea * depth
  }

  // React Hook Form with Zod validation
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<PondUpdateFormData>({
    resolver: zodResolver(pondUpdateSchema),
  })

  const { data: ponds, isLoading } = useQuery({
    queryKey: queryKeys.ponds.all(farmId || ""),
    queryFn: () => pondsApi.list(farmId!),
    enabled: !!farmId,
  })

  const createMutation = useMutation({
    mutationFn: (data: PondCreateRequest) => pondsApi.create(farmId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ponds.all(farmId!),
      })
      setIsCreateDialogOpen(false)
      setCreateStep(1)
      setNewPondName("")
      setPondShape('rectangular')
      setNewPondLength("")
      setNewPondWidth("")
      setNewPondDiameter("")
      setNewPondSurfaceArea("")
      setNewPondVolume("")
      setNewPondDepth("")
      setNewPondDescription("")
      setNewPondFishType("")
      setNewPondWaterType('fresh')
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: { pondId: string; updates: PondUpdateRequest }) =>
      pondsApi.update(farmId!, data.pondId, data.updates),
    onSuccess: (updatedPond) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ponds.all(farmId!),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.ponds.detail(farmId!, updatedPond.id),
      })
      setIsEditMode(false)
      reset()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (pondId: string) => pondsApi.delete(farmId!, pondId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ponds.all(farmId!),
      })
      setIsDeleteDialogOpen(false)
      closePond()
    },
  })

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()

    const requestData: PondCreateRequest = {
      name: newPondName,
      pondShape: pondShape,
      description: newPondDescription || undefined,
      fishType: newPondFishType,
      waterType: newPondWaterType,
    }

    if (pondShape === 'rectangular') {
      const length = parseFloat(newPondLength) || 0
      const width = parseFloat(newPondWidth) || 0
      const depth = parseFloat(newPondDepth) || 0

      // Send numeric dimensions
      requestData.length = length
      requestData.width = width
      requestData.depth = depth

      // Calculate and send volume/surfaceArea
      requestData.surfaceArea = length * width
      requestData.volume = length * width * depth

    } else if (pondShape === 'circular') {
      const diameter = parseFloat(newPondDiameter) || 0
      const depth = parseFloat(newPondDepth) || 0
      const radius = diameter / 2

      // Send numeric dimensions
      requestData.diameter = diameter
      requestData.depth = depth

      // Calculate and send volume/surfaceArea
      requestData.surfaceArea = Math.PI * radius * radius
      requestData.volume = Math.PI * radius * radius * depth

    } else if (pondShape === 'irregular') {
      const surfaceArea = parseFloat(newPondSurfaceArea) || 0
      const volume = parseFloat(newPondVolume) || 0

      // Send numeric values directly
      requestData.surfaceArea = surfaceArea
      requestData.volume = volume
    }

    createMutation.mutate(requestData)
  }

  const handleEditClick = () => {
    if (!pond) return
    reset({
      name: pond.name,
      description: pond.description || "",
    })
    setIsEditMode(true)
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
    reset()
  }

  const onSubmitUpdate = (data: PondUpdateFormData) => {
    if (!pond) return
    updateMutation.mutate({
      pondId: pond.id,
      updates: {
        name: data.name,
        description: data.description || undefined,
      },
    })
  }

  const handleModalClose = () => {
    closePond()
    setIsEditMode(false)
    setIsDeleteDialogOpen(false)
    reset()
  }

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    if (!pond) return
    deleteMutation.mutate(pond.id)
  }

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false)
  }

  // Show loading state while fetching farm
  if (isFarmLoading) {
    return <PageLoading message="Loading farm..." />
  }

  // Show error if no farm found
  if (!farm && farmId && !isFarmLoading) {
    // Determine error message based on error type
    const errorWithResponse = farmError as { response?: { status?: number } } | null
    const errorStatus = errorWithResponse?.response?.status
    let errorTitle = "Farm not found"
    let errorMessage = "The farm you're looking for doesn't exist or you don't have access to it."

    if (isFarmError) {
      if (errorStatus === 404) {
        errorTitle = "Farm not found"
        errorMessage = "This farm doesn't exist. It may have been deleted or the link is incorrect."
      } else if (errorStatus === 403) {
        errorTitle = "Access denied"
        errorMessage = "You don't have permission to view this farm. Please contact the farm owner for access."
      } else if (errorStatus && errorStatus >= 500) {
        errorTitle = "Server error"
        errorMessage = "Unable to load farm data. Please try again later."
      } else {
        errorTitle = "Error loading farm"
        errorMessage = "Something went wrong while loading the farm. Please try again."
      }
    }

    return (
      <Card>
        <div className="text-center py-16">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-lg bg-red-100 border border-red-200 mb-4">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
          </div>
          <h3 className="font-roboto text-base font-semibold text-gray-900">
            {errorTitle}
          </h3>
          <p className="font-roboto mt-2 text-sm text-gray-600 max-w-md mx-auto">
            {errorMessage}
          </p>
          <div className="mt-6">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/farms'}
            >
              Go to My Farms
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  // Show farm selection guard if no farm
  if (!farm) {
    return (
      <FarmRequiredGuard farmId={farmId} isLoading={isFarmLoading}>
        {null}
      </FarmRequiredGuard>
    )
  }

  if (isLoading) {
    return <SectionLoading message="Loading ponds..." />
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-roboto text-3xl font-bold text-gray-900">Ponds</h1>
          <p className="font-roboto mt-2 text-base text-gray-600">
            Manage ponds for {farm.name}
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Create Pond
        </Button>
      </div>

      {/* Ponds Grid */}
      {ponds && ponds.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
          {ponds.map((pond) => (
            <Card
              key={pond.id}
              className="group cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              onClick={() => navigateToPond(pond.id)}
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-secondary-500/10 to-primary-500/10 group-hover:from-secondary-500/20 group-hover:to-primary-500/20 transition-all duration-200">
                      <svg className="h-6 w-6 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                      </svg>
                    </div>
                    <h3 className="font-roboto text-lg font-semibold text-gray-900 group-hover:text-primary-700 transition-colors">{pond.name}</h3>
                  </div>
                  <Badge variant={pond.status === "active" ? "success" : "default"}>
                    {pond.status || "active"}
                  </Badge>
                </div>

                {/* Description with 3-line truncation */}
                <div className="text-sm">
                  {pond.description ? (
                    <p className="font-roboto text-gray-700 line-clamp-3">
                      {pond.description}
                    </p>
                  ) : (
                    <p className="font-roboto text-gray-400 italic">
                      No description
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100">
                  <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="font-roboto text-xs text-gray-500">
                    Created {formatDate(pond.createdAt)}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-dashed border-2">
          <div className="text-center py-16">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-lg bg-gray-100 border border-gray-200 mb-4">
              <svg
                className="h-8 w-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
                />
              </svg>
            </div>
            <h3 className="font-roboto text-base font-semibold text-gray-900">No ponds yet</h3>
            <p className="font-roboto mt-2 text-sm text-gray-600 max-w-sm mx-auto">
              Get started by creating your first pond.
            </p>
            <div className="mt-6">
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Create Your First Pond
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Pond Details Modal */}
      {pond && pondId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={handleModalClose}>
          <div className="w-full max-w-[95vw] md:max-w-2xl lg:max-w-3xl" onClick={(e) => e.stopPropagation()}>
          <Card className="max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4 flex-1">
                <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br from-secondary-500/10 to-primary-500/10 flex-shrink-0">
                  <svg className="h-8 w-8 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  {isEditMode ? (
                    <div>
                      <input
                        type="text"
                        {...register("name")}
                        className={cn(
                          "font-roboto w-full text-2xl font-bold border-b-2 bg-transparent pb-1 mb-2",
                          "focus:outline-none transition-colors",
                          errors.name
                            ? "border-red-500 text-red-900"
                            : "border-gray-300 text-gray-900 focus:border-primary-500"
                        )}
                        placeholder="Pond name"
                      />
                      {errors.name && (
                        <p className="font-roboto text-sm text-red-600 mt-1">{errors.name.message}</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <h2 className="font-roboto text-2xl font-bold text-gray-900">{pond.name}</h2>
                      <Badge variant={pond.status === "active" ? "success" : "default"} className="mt-2">
                        {pond.status || "active"}
                      </Badge>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={handleModalClose}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors flex-shrink-0"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Description */}
              <div>
                <h3 className="font-roboto text-sm font-semibold text-gray-900 mb-2">Description</h3>
                {isEditMode ? (
                  <div>
                    <textarea
                      {...register("description")}
                      rows={4}
                      maxLength={255}
                      className={cn(
                        "font-roboto w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5",
                        "text-gray-900 placeholder-gray-400",
                        "focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20",
                        "transition-colors duration-200 resize-none"
                      )}
                      placeholder="Enter pond description..."
                    />
                    <div className="flex justify-between mt-1">
                      <p className="font-roboto text-xs text-gray-500">
                        {(watch("description") || "").length}/255 characters
                      </p>
                      {errors.description && (
                        <p className="font-roboto text-xs text-red-600">
                          {errors.description.message}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {pond.description ? (
                      <p className="font-roboto text-gray-700 text-sm leading-relaxed">
                        {pond.description}
                      </p>
                    ) : (
                      <p className="font-roboto text-gray-400 italic text-sm">
                        No description
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Dimensions */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="font-roboto text-sm font-semibold text-gray-900">Dimensions</h3>
                  <div className="inline-flex items-center gap-2 rounded-lg bg-primary-50/50 border border-primary-200/50 px-3 py-1.5">
                    {pond.pondShape === 'rectangular' && (
                      <>
                        <svg className="h-4 w-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <rect x="4" y="6" width="16" height="12" rx="1" />
                        </svg>
                        <span className="font-roboto text-xs font-medium text-primary-700 capitalize">Rectangular</span>
                      </>
                    )}
                    {pond.pondShape === 'circular' && (
                      <>
                        <svg className="h-4 w-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <circle cx="12" cy="12" r="8" />
                        </svg>
                        <span className="font-roboto text-xs font-medium text-primary-700 capitalize">Circular</span>
                      </>
                    )}
                    {pond.pondShape === 'irregular' && (
                      <>
                        <svg className="h-4 w-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 8 l3-2 4 3 5-1 3 4 -2 5 -4 1 -5-2 -4-3z" />
                        </svg>
                        <span className="font-roboto text-xs font-medium text-primary-700 capitalize">Irregular</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Rectangular Pond */}
                {pond.pondShape === 'rectangular' && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-lg bg-gradient-to-br from-blue-50 to-blue-100/50 p-3 border border-blue-200/50">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12M8 12h12M8 17h12M4 7h.01M4 12h.01M4 17h.01" />
                        </svg>
                        <span className="font-roboto text-xs font-medium text-blue-700">Length</span>
                      </div>
                      <p className="font-roboto text-xl font-bold text-blue-900">
                        {(pond.length ?? 0) > 0 ? `${pond.length?.toFixed(1)}m` : "—"}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gradient-to-br from-green-50 to-green-100/50 p-3 border border-green-200/50">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16M4 16h16" />
                        </svg>
                        <span className="font-roboto text-xs font-medium text-green-700">Width</span>
                      </div>
                      <p className="font-roboto text-xl font-bold text-green-900">
                        {(pond.width ?? 0) > 0 ? `${pond.width?.toFixed(1)}m` : "—"}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gradient-to-br from-purple-50 to-purple-100/50 p-3 border border-purple-200/50">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="h-4 w-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                        </svg>
                        <span className="font-roboto text-xs font-medium text-purple-700">Depth</span>
                      </div>
                      <p className="font-roboto text-xl font-bold text-purple-900">
                        {(pond.depth ?? 0) > 0 ? `${pond.depth?.toFixed(1)}m` : "—"}
                      </p>
                    </div>
                  </div>
                )}

                {/* Circular Pond */}
                {pond.pondShape === 'circular' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-gradient-to-br from-blue-50 to-blue-100/50 p-3 border border-blue-200/50">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <circle cx="12" cy="12" r="8" />
                        </svg>
                        <span className="font-roboto text-xs font-medium text-blue-700">Diameter</span>
                      </div>
                      <p className="font-roboto text-xl font-bold text-blue-900">
                        {(pond.diameter ?? 0) > 0 ? `${pond.diameter?.toFixed(1)}m` : "—"}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gradient-to-br from-purple-50 to-purple-100/50 p-3 border border-purple-200/50">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="h-4 w-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                        </svg>
                        <span className="font-roboto text-xs font-medium text-purple-700">Depth</span>
                      </div>
                      <p className="font-roboto text-xl font-bold text-purple-900">
                        {(pond.depth ?? 0) > 0 ? `${pond.depth?.toFixed(1)}m` : "—"}
                      </p>
                    </div>
                  </div>
                )}

                {/* Irregular Pond */}
                {pond.pondShape === 'irregular' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-gradient-to-br from-amber-50 to-amber-100/50 p-3 border border-amber-200/50">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="h-4 w-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                        <span className="font-roboto text-xs font-medium text-amber-700">Surface Area</span>
                      </div>
                      <p className="font-roboto text-xl font-bold text-amber-900">
                        {pond.surfaceArea ? `${pond.surfaceArea.toFixed(2)}m²` : "—"}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gradient-to-br from-blue-50 to-blue-100/50 p-3 border border-blue-200/50">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        <span className="font-roboto text-xs font-medium text-blue-700">Volume</span>
                      </div>
                      <p className="font-roboto text-xl font-bold text-blue-900">
                        {pond.volume ? `${pond.volume.toFixed(2)}m³` : "—"}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Calculated Metrics - Only for rectangular and circular ponds */}
              {(pond.pondShape === 'rectangular' || pond.pondShape === 'circular') && (
                <div>
                  <h3 className="font-roboto text-sm font-semibold text-gray-900 mb-3">Calculated Metrics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg bg-gray-50 p-4 border border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        <span className="font-roboto text-sm font-medium text-gray-600">Volume</span>
                      </div>
                      <p className="font-roboto text-2xl font-bold text-gray-900">
                        {pond.volume ? `${pond.volume.toFixed(2)}m³` : "—"}
                      </p>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4 border border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                        <span className="font-roboto text-sm font-medium text-gray-600">Surface Area</span>
                      </div>
                      <p className="font-roboto text-2xl font-bold text-gray-900">
                        {pond.surfaceArea ? `${pond.surfaceArea.toFixed(2)}m²` : "—"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-roboto text-gray-600">Created:</span>
                  <span className="font-roboto font-medium text-gray-900">{formatDate(pond.createdAt)}</span>
                </div>
                {pond.updatedAt && (
                  <div className="flex items-center gap-2 text-sm">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span className="font-roboto text-gray-600">Last Updated:</span>
                    <span className="font-roboto font-medium text-gray-900">{formatDate(pond.updatedAt)}</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {updateMutation.isError && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="font-roboto text-sm text-red-800">
                    Failed to update pond. Please try again.
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                {isEditMode ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancelEdit}
                      disabled={updateMutation.isPending}
                      className="flex-1 w-full sm:w-auto"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSubmit(onSubmitUpdate)}
                      disabled={updateMutation.isPending}
                      className="flex-1 w-full sm:w-auto"
                    >
                      {updateMutation.isPending ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Save Changes
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      onClick={handleEditClick}
                      className="flex-1 w-full sm:w-auto"
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit Pond
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleDeleteClick}
                      className="flex-1 w-full sm:w-auto border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Card>
          </div>
        </div>
      )}

      {/* Create Pond Dialog */}
      {isCreateDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <Card className="w-full max-w-[95vw] sm:max-w-md">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-roboto text-xl font-semibold text-gray-900">
                {createStep === 1 ? 'Create New Pond' : 'Additional Details'}
              </h2>
              <span className="font-roboto text-sm text-gray-500">
                Step {createStep}/2
              </span>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault()
              if (createStep === 1) {
                setCreateStep(2)
              } else {
                handleCreate(e)
              }
            }} className="space-y-4">
              {/* Step 1: Pond Details & Dimensions */}
              {createStep === 1 && (
                <>
                  <div>
                    <label className="font-roboto mb-1.5 block text-sm font-medium text-gray-700">
                      Pond Name *
                    </label>
                <input
                  type="text"
                  value={newPondName}
                  onChange={(e) => setNewPondName(e.target.value)}
                  required
                  className={cn(
                    "font-roboto w-full rounded-lg border border-gray-200/70 bg-white px-4 py-2.5",
                    "text-gray-900 placeholder-gray-400",
                    "focus:border-primary-500 focus:outline-none focus:ring-0",
                    "transition-colors duration-200"
                  )}
                  placeholder="Enter pond name"
                />
              </div>

              {/* Pond Shape Selector */}
              <div>
                <label className="font-roboto mb-2 block text-sm font-medium text-gray-700">
                  Pond Shape *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {/* Rectangular Button */}
                  <button
                    type="button"
                    onClick={() => setPondShape('rectangular')}
                    className={cn(
                      "flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-all duration-200",
                      pondShape === 'rectangular'
                        ? "border-primary-500 bg-primary-50/50 shadow-sm"
                        : "border-gray-200/50 hover:border-gray-300/70 bg-white"
                    )}
                  >
                    <svg className={cn("h-8 w-8", pondShape === 'rectangular' ? "text-primary-600" : "text-gray-500")} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <rect x="4" y="6" width="16" height="12" rx="1" />
                    </svg>
                    <span className={cn("font-roboto text-xs font-medium", pondShape === 'rectangular' ? "text-primary-700" : "text-gray-600")}>
                      Rectangular
                    </span>
                  </button>

                  {/* Circular Button */}
                  <button
                    type="button"
                    onClick={() => setPondShape('circular')}
                    className={cn(
                      "flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-all duration-200",
                      pondShape === 'circular'
                        ? "border-primary-500 bg-primary-50/50 shadow-sm"
                        : "border-gray-200/50 hover:border-gray-300/70 bg-white"
                    )}
                  >
                    <svg className={cn("h-8 w-8", pondShape === 'circular' ? "text-primary-600" : "text-gray-500")} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <circle cx="12" cy="12" r="8" />
                    </svg>
                    <span className={cn("font-roboto text-xs font-medium", pondShape === 'circular' ? "text-primary-700" : "text-gray-600")}>
                      Circular
                    </span>
                  </button>

                  {/* Irregular Button */}
                  <button
                    type="button"
                    onClick={() => setPondShape('irregular')}
                    className={cn(
                      "flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-all duration-200",
                      pondShape === 'irregular'
                        ? "border-primary-500 bg-primary-50/50 shadow-sm"
                        : "border-gray-200/50 hover:border-gray-300/70 bg-white"
                    )}
                  >
                    <svg className={cn("h-8 w-8", pondShape === 'irregular' ? "text-primary-600" : "text-gray-500")} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 8 l3-2 4 3 5-1 3 4 -2 5 -4 1 -5-2 -4-3z" />
                    </svg>
                    <span className={cn("font-roboto text-xs font-medium", pondShape === 'irregular' ? "text-primary-700" : "text-gray-600")}>
                      Irregular
                    </span>
                  </button>
                </div>
              </div>

              {/* Conditional Dimensional Inputs */}
              {pondShape === 'rectangular' && (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="font-roboto mb-1.5 block text-sm font-medium text-gray-700">
                        Length (m) *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={newPondLength}
                        onChange={(e) => setNewPondLength(e.target.value)}
                        required
                        className={cn(
                          "font-roboto w-full rounded-lg border border-gray-200/70 bg-white px-4 py-2.5",
                          "text-gray-900 placeholder-gray-400",
                          "focus:border-primary-500 focus:outline-none focus:ring-0",
                          "transition-colors duration-200"
                        )}
                        placeholder="0.0"
                      />
                    </div>
                    <div>
                      <label className="font-roboto mb-1.5 block text-sm font-medium text-gray-700">
                        Width (m) *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={newPondWidth}
                        onChange={(e) => setNewPondWidth(e.target.value)}
                        required
                        className={cn(
                          "font-roboto w-full rounded-lg border border-gray-200/70 bg-white px-4 py-2.5",
                          "text-gray-900 placeholder-gray-400",
                          "focus:border-primary-500 focus:outline-none focus:ring-0",
                          "transition-colors duration-200"
                        )}
                        placeholder="0.0"
                      />
                    </div>
                    <div>
                      <label className="font-roboto mb-1.5 block text-sm font-medium text-gray-700">
                        Depth (m) *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={newPondDepth}
                        onChange={(e) => setNewPondDepth(e.target.value)}
                        required
                        className={cn(
                          "font-roboto w-full rounded-lg border border-gray-200/70 bg-white px-4 py-2.5",
                          "text-gray-900 placeholder-gray-400",
                          "focus:border-primary-500 focus:outline-none focus:ring-0",
                          "transition-colors duration-200"
                        )}
                        placeholder="0.0"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-roboto text-gray-600">Calculated Volume:</span>
                    <span className="font-roboto font-semibold text-gray-900">{calculatedVolume().toFixed(2)} m³</span>
                  </div>
                </>
              )}

              {pondShape === 'circular' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="font-roboto mb-1.5 block text-sm font-medium text-gray-700">
                        Diameter (m) *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={newPondDiameter}
                        onChange={(e) => setNewPondDiameter(e.target.value)}
                        required
                        className={cn(
                          "font-roboto w-full rounded-lg border border-gray-200/70 bg-white px-4 py-2.5",
                          "text-gray-900 placeholder-gray-400",
                          "focus:border-primary-500 focus:outline-none focus:ring-0",
                          "transition-colors duration-200"
                        )}
                        placeholder="0.0"
                      />
                      <p className="font-roboto text-xs text-gray-500 mt-1">
                        Measure across the widest part
                      </p>
                    </div>
                    <div>
                      <label className="font-roboto mb-1.5 block text-sm font-medium text-gray-700">
                        Depth (m) *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={newPondDepth}
                        onChange={(e) => setNewPondDepth(e.target.value)}
                        required
                        className={cn(
                          "font-roboto w-full rounded-lg border border-gray-200/70 bg-white px-4 py-2.5",
                          "text-gray-900 placeholder-gray-400",
                          "focus:border-primary-500 focus:outline-none focus:ring-0",
                          "transition-colors duration-200"
                        )}
                        placeholder="0.0"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-roboto text-gray-600">Calculated Volume:</span>
                    <span className="font-roboto font-semibold text-gray-900">{calculatedVolume().toFixed(2)} m³</span>
                  </div>
                </>
              )}

              {pondShape === 'irregular' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-roboto mb-1.5 block text-sm font-medium text-gray-700">
                      Surface Area (m²) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={newPondSurfaceArea}
                      onChange={(e) => setNewPondSurfaceArea(e.target.value)}
                      required
                      className={cn(
                        "font-roboto w-full rounded-lg border border-gray-200/70 bg-white px-4 py-2.5",
                        "text-gray-900 placeholder-gray-400",
                        "focus:border-primary-500 focus:outline-none focus:ring-0",
                        "transition-colors duration-200"
                      )}
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="font-roboto mb-1.5 block text-sm font-medium text-gray-700">
                      Volume (m³) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={newPondVolume}
                      onChange={(e) => setNewPondVolume(e.target.value)}
                      required
                      className={cn(
                        "font-roboto w-full rounded-lg border border-gray-200/70 bg-white px-4 py-2.5",
                        "text-gray-900 placeholder-gray-400",
                        "focus:border-primary-500 focus:outline-none focus:ring-0",
                        "transition-colors duration-200"
                      )}
                      placeholder="0.0"
                    />
                  </div>
                </div>
              )}

              {/* Fish Type */}
              <div>
                <label className="font-roboto mb-1.5 block text-sm font-medium text-gray-700">
                  Fish Type *
                </label>
                <input
                  type="text"
                  value={newPondFishType}
                  onChange={(e) => setNewPondFishType(e.target.value)}
                  required
                  className={cn(
                    "font-roboto w-full rounded-lg border border-gray-200/70 bg-white px-4 py-2.5",
                    "text-gray-900 placeholder-gray-400",
                    "focus:border-primary-500 focus:outline-none focus:ring-0",
                    "transition-colors duration-200"
                  )}
                  placeholder="e.g., Tilapia, Catfish, Bass"
                />
              </div>

              {/* Water Type */}
              <div>
                <label className="font-roboto mb-1.5 block text-sm font-medium text-gray-700">
                  Water Type *
                </label>
                <select
                  value={newPondWaterType}
                  onChange={(e) => setNewPondWaterType(e.target.value as 'fresh' | 'salt' | 'brackish')}
                  required
                  className={cn(
                    "font-roboto w-full rounded-lg border border-gray-200/70 bg-white px-4 py-2.5",
                    "text-gray-900",
                    "focus:border-primary-500 focus:outline-none focus:ring-0",
                    "transition-colors duration-200"
                  )}
                >
                  <option value="fresh">Freshwater</option>
                  <option value="salt">Saltwater</option>
                  <option value="brackish">Brackish</option>
                </select>
              </div>
                </>
              )}

              {/* Step 2: Additional Information */}
              {createStep === 2 && (
                <>
                  <div>
                    <label className="font-roboto mb-1.5 block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      value={newPondDescription}
                      onChange={(e) => setNewPondDescription(e.target.value)}
                      maxLength={255}
                      rows={5}
                  className={cn(
                    "font-roboto w-full rounded-lg border border-gray-200/70 bg-white px-4 py-2.5",
                    "text-gray-900 placeholder-gray-400",
                    "focus:border-primary-500 focus:outline-none focus:ring-0",
                    "transition-colors duration-200 resize-none"
                  )}
                  placeholder="Enter pond description (optional)"
                />
                <p className="font-roboto text-xs text-gray-500 mt-1">
                  {newPondDescription.length}/255 characters
                </p>
              </div>
                </>
              )}

              {/* Error Message (shown on step 2 only) */}
              {createStep === 2 && createMutation.isError && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="font-roboto text-sm text-red-800">
                    Failed to create pond. Please try again.
                  </p>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex gap-3">
                {createStep === 1 ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsCreateDialogOpen(false)
                        setCreateStep(1)
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1"
                    >
                      Next
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCreateStep(1)}
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={createMutation.isPending}
                      className="flex-1"
                    >
                      {createMutation.isPending ? "Creating..." : "Create"}
                    </Button>
                  </>
                )}
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {isDeleteDialogOpen && pond && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4" onClick={handleCancelDelete}>
          <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
          <Card>
            <div className="flex items-start gap-4 mb-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-roboto text-lg font-semibold text-gray-900 mb-1">
                  Delete Pond
                </h3>
                <p className="font-roboto text-sm text-gray-600">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">{pond.name}</span>? This action cannot be undone.
                </p>
              </div>
            </div>

            {deleteMutation.isError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-3 mb-4">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-roboto text-sm text-red-800">
                  Failed to delete pond. Please try again.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelDelete}
                disabled={deleteMutation.isPending}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleteMutation.isPending}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteMutation.isPending ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Pond
                  </>
                )}
              </Button>
            </div>
          </Card>
          </div>
        </div>
      )}
    </div>
  )
}
